import React from "react";
import { FaBus, FaInfoCircle, FaPhone, FaUserTie } from "react-icons/fa";

// Shared presentation for event bus tracking, kept identical to the live
// transport tracking modal so parents read the same card in both places.

export const EventBusArrivingCard = ({ info }) => {
  if (!info) return null;

  const {
    hasSubstitute,
    substitute,
    activeVehicleNumber,
    originalVehicleNumber,
  } = info;

  if (!activeVehicleNumber && !hasSubstitute) return null;

  return (
    <div
      className="bg-white rounded-4 border shadow-sm mb-3 overflow-hidden"
      style={{ borderLeft: `4px solid ${hasSubstitute ? "#f7a600" : "#3d5ee1"}` }}
    >
      <div className="d-flex align-items-center gap-3 p-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
          style={{ width: "44px", height: "44px", backgroundColor: hasSubstitute ? "#fff5e0" : "#eceffd" }}
        >
          <FaBus size={20} className={hasSubstitute ? "text-warning" : "text-primary"} />
        </div>

        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <span className="text-muted text-uppercase d-block" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
            Bus Arriving Now
          </span>
          <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
            <strong className="text-dark" style={{ fontSize: "17px", letterSpacing: "0.5px" }}>
              {activeVehicleNumber || "N/A"}
            </strong>
            {hasSubstitute ? (
              <span className="badge bg-warning text-dark" style={{ fontSize: "9px" }}>Substitute Bus</span>
            ) : (
              <span className="badge bg-secondary" style={{ fontSize: "9px" }}>Original Bus</span>
            )}
            {hasSubstitute && substitute?.hasTrip && (
              <span className="badge bg-light text-dark border" style={{ fontSize: "9px" }}>
                {substitute.tripLabel}
              </span>
            )}
          </div>
        </div>

        {hasSubstitute && substitute?.substituteDateLabel && (
          <div className="text-end flex-shrink-0">
            <span className="text-muted text-uppercase d-block" style={{ fontSize: "9px", letterSpacing: "0.5px" }}>
              Substitute Date
            </span>
            <strong className="text-dark d-block" style={{ fontSize: "12px" }}>
              {substitute.substituteDateLabel}
            </strong>
          </div>
        )}
      </div>

      {hasSubstitute && originalVehicleNumber && (
        <div
          className="d-flex align-items-center gap-2 px-3 py-2 border-top text-muted"
          style={{ fontSize: "11px", backgroundColor: "#fbfbfd" }}
        >
          <FaInfoCircle size={12} className="text-warning flex-shrink-0" />
          <span>
            Regular bus <strong className="text-dark">{originalVehicleNumber}</strong> is not running for this trip.
          </span>
        </div>
      )}
    </div>
  );
};

export const EventBusContactCard = ({ info }) => {
  if (!info) return null;

  const { hasSubstitute, substitute, activeVehicleNumber, contacts = [] } = info;

  if (!activeVehicleNumber && !contacts.length) return null;

  return (
    <div className="bg-white p-3 rounded-4 border shadow-sm mt-3">
      <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
        <FaUserTie className="text-primary" />
        Bus &amp; Contact Information
      </h6>
      <div className="row g-3 text-dark small">
        <div className="col-6 col-md-6">
          <span className="text-muted d-block">Tracked Bus:</span>
          <strong className="text-dark fs-14">
            {activeVehicleNumber || "--"}
            {hasSubstitute ? (
              <span className="badge bg-warning text-dark ms-1" style={{ fontSize: "9px" }}>Substitute Bus</span>
            ) : (
              <span className="badge bg-secondary ms-1" style={{ fontSize: "9px" }}>Original Bus</span>
            )}
          </strong>
        </div>

        {hasSubstitute && substitute?.hasTrip && (
          <div className="col-6 col-md-6">
            <span className="text-muted d-block">Service Type:</span>
            <strong className="text-dark fs-14">{substitute.tripLabel}</strong>
          </div>
        )}

        {contacts.length > 0 ? (
          contacts.map((item, idx) => (
            <div className="col-6 col-md-6" key={`${item.label}-${idx}`}>
              <span className={`d-block ${item.highlight ? "text-warning font-weight-bold" : "text-muted"}`}>
                {item.label}:
              </span>
              <strong className="text-dark fs-14 d-block">{item.name || "--"}</strong>
              {item.phone && (
                <a href={`tel:${item.phone}`} className="text-primary fw-semibold">
                  <FaPhone className="me-1" size={12} />
                  {item.phone}
                </a>
              )}
              {item.note && (
                <span className="text-muted d-block" style={{ fontSize: "10px" }}>({item.note})</span>
              )}
            </div>
          ))
        ) : (
          <div className="col-12">
            <span className="text-muted">
              Driver &amp; attendee contact details have not been shared by the school admin yet.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventBusArrivingCard;
